import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

interface CampaignAveragesProps {
  campaignAverages: {
    startDate?: string;
    endDate?: string;
    days: number;
    avgImpressions: number;
    avgClicks: number;
    avgCpc: number;
    avgConversions: number;
    avgCost: number;
    avgCtr: number;
    avgCvr: number;
    totalImpressions: number;
    totalClicks: number;
    totalConversions: number;
    totalCost: number;
  } | null;
  searchTermsLength: number;
  isLoading: boolean;
}

// Format date inline using Intl.DateTimeFormat for day with ordinal suffix
const formatDate = (dateString?: string): string => 
  dateString ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(dateString)) : 'unknown date';

export const CampaignAverages = ({ campaignAverages, searchTermsLength, isLoading }: CampaignAveragesProps) => {
  const { symbol: currencySymbol } = useCurrencySymbol('google');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 mb-4 bg-white rounded-md border">
        <Spinner />
        <span className="ml-2">Calculating averages...</span>
      </div>
    );
  }

  if (!campaignAverages) {
    return (
      <div className="text-center p-4 bg-muted rounded-md mb-6">
        <p className="text-sm text-muted-foreground">No campaign data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium">Campaign Averages</h3>
        </div>
        <div className="text-xs text-muted-foreground">
          Based on {searchTermsLength} keywords between {formatDate(campaignAverages.startDate)} and {formatDate(campaignAverages.endDate)} inclusive ({campaignAverages.days} total days)
        </div>
      </div>

      <div className="rounded-md border mb-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/4">Metric</TableHead>
              <TableHead className="w-[15%] text-right">Average Per Term</TableHead>
              <TableHead className="w-[15%] text-right">Campaign Total</TableHead>
              <TableHead className="w-[15%] text-right">Good Benchmark</TableHead>
              <TableHead className="w-[15%] text-right">Warning Level</TableHead>
              <TableHead className="w-[15%] text-right">Critical Level</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="border-b border-gray-200">
              <TableCell className="font-medium">Impressions</TableCell>
              <TableCell className="text-right">{Math.round(campaignAverages.avgImpressions || 0).toLocaleString()}</TableCell>
              <TableCell className="text-right">{campaignAverages.totalImpressions ? campaignAverages.totalImpressions.toLocaleString() : '-'}</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&gt; {Math.round((campaignAverages.avgImpressions || 0) * 3).toLocaleString()}</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&gt; {Math.round((campaignAverages.avgImpressions || 0) * 5).toLocaleString()}</span></TableCell>
            </TableRow>
            <TableRow className="border-b-0">
              <TableCell className="font-medium">Clicks</TableCell>
              <TableCell className="text-right">{Math.round(campaignAverages.avgClicks || 0).toLocaleString()}</TableCell>
              <TableCell className="text-right">{typeof campaignAverages.totalClicks === 'number' ? campaignAverages.totalClicks.toLocaleString() : '-'}</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&gt; {Math.round((campaignAverages.avgClicks || 0) * 2).toLocaleString()}</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&gt; {Math.round((campaignAverages.avgClicks || 0) * 4).toLocaleString()}</span></TableCell>
            </TableRow>
            <TableRow className="border-b-0">
              <TableCell className="font-medium">Clickthrough Rate (CTR)</TableCell>
              <TableCell className="text-right">{((campaignAverages.avgCtr || 0) * 100).toFixed(2)}%</TableCell>
              <TableCell className="text-right">{campaignAverages.totalImpressions > 0 ? ((campaignAverages.totalClicks / campaignAverages.totalImpressions) * 100).toFixed(2) : '0.00'}%</TableCell>
              <TableCell className="text-right"><span className="text-green-600">&gt; {((campaignAverages.avgCtr || 0) * 100 * 1.2).toFixed(2)}%</span></TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&lt; {((campaignAverages.avgCtr || 0) * 100 * 0.5).toFixed(2)}%</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&lt; {((campaignAverages.avgCtr || 0) * 100 * 0.2).toFixed(2)}%</span></TableCell>
            </TableRow>
            <TableRow className="border-b border-gray-200">
              <TableCell className="font-medium">Cost per Click (CPC)</TableCell>
              <TableCell className="text-right">{currencySymbol}{campaignAverages ? (campaignAverages.avgCpc || 0).toFixed(2) : '0.00'}</TableCell>
              <TableCell className="text-right">{currencySymbol}{campaignAverages.totalClicks > 0 ? (campaignAverages.totalCost / campaignAverages.totalClicks).toFixed(2) : '-'}</TableCell>
              <TableCell className="text-right"><span className="text-green-600">&lt; {currencySymbol}{((campaignAverages.avgCost / (campaignAverages.avgClicks || 1)) * 0.8).toFixed(2)}</span></TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&gt; {currencySymbol}{((campaignAverages.avgCost / (campaignAverages.avgClicks || 1)) * 1.5).toFixed(2)}</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&gt; {currencySymbol}{((campaignAverages.avgCost / (campaignAverages.avgClicks || 1)) * 2.5).toFixed(2)}</span></TableCell>
            </TableRow>
            <TableRow className="border-b-0">
              <TableCell className="font-medium">Conversions</TableCell>
              <TableCell className="text-right">{Math.round(campaignAverages.avgConversions || 0).toLocaleString()}</TableCell>
              <TableCell className="text-right">{typeof campaignAverages.totalConversions === 'number' ? campaignAverages.totalConversions.toLocaleString() : '-'}</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&gt; {Math.round((campaignAverages.avgConversions || 0) * 2).toLocaleString()}</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&gt; {Math.round((campaignAverages.avgConversions || 0) * 4).toLocaleString()}</span></TableCell>
            </TableRow>
            <TableRow className="border-b-0">
              <TableCell className="font-medium">Conversion Rate (CVR)</TableCell>
              <TableCell className="text-right">{((campaignAverages.avgCvr || 0) * 100).toFixed(2)}%</TableCell>
              <TableCell className="text-right">{campaignAverages.totalClicks > 0 ? ((campaignAverages.totalConversions / campaignAverages.totalClicks) * 100).toFixed(2) : '0.00'}%</TableCell>
              <TableCell className="text-right"><span className="text-green-600">&gt; {((campaignAverages.avgCvr || 0) * 100 * 1.2).toFixed(2)}%</span></TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&lt; {((campaignAverages.avgCvr || 0) * 100 * 0.5).toFixed(2)}%</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&lt; {((campaignAverages.avgCvr || 0) * 100 * 0.2).toFixed(2)}%</span></TableCell>
            </TableRow>
            <TableRow className="border-b border-gray-200">
              <TableCell className="font-medium">Cost Per Conversion (CPA)</TableCell>
              <TableCell className="text-right">
                {campaignAverages.avgConversions > 0
                  ? `${currencySymbol}${(campaignAverages.avgCost / campaignAverages.avgConversions).toFixed(2)}`
                  : '-'}
              </TableCell>
              <TableCell className="text-right">
                {campaignAverages.totalConversions > 0
                  ? `${currencySymbol}${(campaignAverages.totalCost / campaignAverages.totalConversions).toFixed(2)}`
                  : '-'}
              </TableCell>
              <TableCell className="text-right">
                {campaignAverages.avgConversions > 0 ? (
                  <span className="text-green-600">&lt; {currencySymbol}{(campaignAverages.avgCost / campaignAverages.avgConversions * 0.8).toFixed(2)}</span>
                ) : '-'}
              </TableCell>
              <TableCell className="text-right">
                {campaignAverages.avgConversions > 0 ? (
                  <span className="text-yellow-600">&gt; {currencySymbol}{(campaignAverages.avgCost / campaignAverages.avgConversions * 1.5).toFixed(2)}</span>
                ) : '-'}
              </TableCell>
              <TableCell className="text-right">
                {campaignAverages.avgConversions > 0 ? (
                  <span className="text-red-600">&gt; {currencySymbol}{(campaignAverages.avgCost / campaignAverages.avgConversions * 3).toFixed(2)}</span>
                ) : '-'}
              </TableCell>
            </TableRow>
            <TableRow className="border-b-0">
              <TableCell className="font-medium">Total Cost</TableCell>
              <TableCell className="text-right">{currencySymbol}{(campaignAverages.avgCost || 0).toFixed(2)}</TableCell>
              <TableCell className="text-right">{currencySymbol}{(campaignAverages.totalCost || 0).toFixed(2)}</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right"><span className="text-yellow-600">&gt; {currencySymbol}{((campaignAverages.avgCost || 0) * 2).toFixed(2)}</span></TableCell>
              <TableCell className="text-right"><span className="text-red-600">&gt; {currencySymbol}{((campaignAverages.avgCost || 0) * 4).toFixed(2)}</span></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}; 